/**
 * SVG Profile Exporter
 *
 * Renders a CrossSectionProfile data model as a clean, annotated SVG drawing.
 * Produces self-contained SVG compatible with Visio, Inkscape, and browsers.
 *
 * Uses D3 v3 (loaded globally) for scales, axes, and line generation.
 */

// SVG document dimensions
const SVG_WIDTH = 1200;
const SVG_HEIGHT = 800;

// Layout margins
const MARGIN = {
	top: 50,
	right: 220,
	bottom: 120,
	left: 80,
};

// Minimum plot height to keep the profile line visible
const MIN_PLOT_HEIGHT = 60;

// Colors for annotation layers
const COLORS = {
	profileLine: '#333333',
	pointScatter: '#999999',
	landmark: '#0066ff',
	curbReveal: '#00cc66',
	datumRef: '#cc6600',
	derivedLandmark: '#9933cc',
	highPoint: '#cc0000',
	lowPoint: '#0000cc',
	slope: '#666666',
	grid: '#e0e0e0',
	axis: '#333333',
	scaleBar: '#333333',
	notes: '#666666',
};

// Font settings (system fonts for cross-app compatibility)
const FONT_FAMILY = 'Arial, Helvetica, sans-serif';

// Target number of smoothed output points
const SMOOTH_TARGET_POINTS = 300;

export class SVGProfileExporter {

	/**
	 * Generate an SVG element from a CrossSectionProfile.
	 *
	 * @param {Object} profile - CrossSectionProfile data model.
	 * @param {Object} options - Optional settings.
	 * @param {string} options.title - Optional title for the drawing.
	 * @param {string} options.notes - Optional free-text notes.
	 * @param {boolean} options.showPoints - Show raw point scatter (default false).
	 * @returns {SVGElement} The generated SVG DOM element.
	 */
	static toSVG(profile, options = {}) {
		const layout = SVGProfileExporter._computeLayout(profile, options);
		const scales = SVGProfileExporter._createScales(profile, layout);
		const svgEl = SVGProfileExporter._createSVGDocument(layout.svgHeight);

		// D3 selection on the detached SVG element
		const svg = d3.select(svgEl);

		// White background
		svg.append('rect')
			.attr('width', SVG_WIDTH)
			.attr('height', layout.svgHeight)
			.attr('fill', '#ffffff');

		// Title block
		if (options.title) {
			SVGProfileExporter._renderTitleBlock(svg, options.title, layout);
		}

		// Grid
		SVGProfileExporter._renderGrid(svg, scales, layout);

		// Axes
		SVGProfileExporter._renderAxes(svg, scales, layout, profile.datum_offset);

		// Optional raw point scatter (behind the line)
		if (options.showPoints && profile.points && profile.points.length > 0) {
			SVGProfileExporter._renderPointScatter(svg, profile.points, scales);
		}

		// Smoothed profile line (always rendered)
		SVGProfileExporter._renderProfileLine(svg, profile.points, scales);

		// Annotation layers (conditional on data presence)
		const activeLayers = [];

		if (profile.intersecting_landmarks && profile.intersecting_landmarks.length > 0) {
			SVGProfileExporter._renderLandmarks(svg, profile.intersecting_landmarks, scales);
			activeLayers.push('landmarks');
		}

		if (profile.intersecting_curb_reveals && profile.intersecting_curb_reveals.length > 0) {
			SVGProfileExporter._renderCurbReveals(svg, profile.intersecting_curb_reveals, scales, layout);
			activeLayers.push('curbReveals');
		}

		if (profile.intersecting_landmarks) {
			const datumLandmarks = profile.intersecting_landmarks.filter(lm => lm.datum != null);
			if (datumLandmarks.length > 0) {
				SVGProfileExporter._renderDatumReferences(svg, datumLandmarks, scales, layout);
				activeLayers.push('datumReferences');
			}
		}

		if (profile.intersecting_landmarks) {
			const derived = profile.intersecting_landmarks.filter(lm => lm.derived_from != null);
			if (derived.length > 0) {
				SVGProfileExporter._renderDerivedLandmarks(svg, derived, scales, profile.intersecting_landmarks);
				activeLayers.push('derivedLandmarks');
			}
		}

		if (profile.computed_metrics) {
			if (profile.computed_metrics.high_point || profile.computed_metrics.low_point) {
				SVGProfileExporter._renderHighLowPoints(svg, profile.computed_metrics, scales);
				activeLayers.push('highLowPoints');
			}
		}

		// Legend (auto-generated from active layers)
		if (activeLayers.length > 0) {
			SVGProfileExporter._renderLegend(svg, activeLayers, layout);
		}

		// Notes
		if (options.notes) {
			SVGProfileExporter._renderNotes(svg, options.notes, layout);
		}

		return svgEl;
	}

	/**
	 * Generate an SVG string from a CrossSectionProfile.
	 *
	 * @param {Object} profile - CrossSectionProfile data model.
	 * @param {Object} options - Optional settings.
	 * @returns {string} The SVG as a string with XML declaration.
	 */
	static toString(profile, options = {}) {
		const svgEl = SVGProfileExporter.toSVG(profile, options);
		const serializer = new XMLSerializer();
		const svgString = serializer.serializeToString(svgEl);
		return '<?xml version="1.0" encoding="UTF-8"?>\n' + svgString;
	}

	// ─── SVG Document Setup ────────────────────────────────────────

	static _createSVGDocument(svgHeight) {
		const svgNS = 'http://www.w3.org/2000/svg';
		const svg = document.createElementNS(svgNS, 'svg');
		svg.setAttribute('xmlns', svgNS);
		svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
		svg.setAttribute('version', '1.1');
		svg.setAttribute('width', '297mm');
		// Scale height proportionally: maintain the same px-to-mm ratio as width
		const heightMm = Math.round(svgHeight * (297 / SVG_WIDTH));
		svg.setAttribute('height', `${heightMm}mm`);
		svg.setAttribute('viewBox',
			`0 0 ${SVG_WIDTH} ${svgHeight}`);
		return svg;
	}

	static _computeLayout(profile, options) {
		const m = MARGIN;
		const hasTitle = !!(options && options.title);
		const hasNotes = !!(options && options.notes);
		const titleHeight = hasTitle ? 40 : 0;
		const notesHeight = hasNotes ? 40 : 0;

		const plotWidth = SVG_WIDTH - m.left - m.right;

		// Compute data extents
		const points = (profile && profile.points) || [];
		let minDist = Infinity, maxDist = -Infinity;
		let minElev = Infinity, maxElev = -Infinity;

		for (const pt of points) {
			if (pt.distance_along < minDist) minDist = pt.distance_along;
			if (pt.distance_along > maxDist) maxDist = pt.distance_along;
			if (pt.elevation < minElev) minElev = pt.elevation;
			if (pt.elevation > maxElev) maxElev = pt.elevation;
		}

		if (points.length === 0) {
			minDist = 0; maxDist = 1;
			minElev = 0; maxElev = 1;
		}

		// Add 5% padding (same as _createScales used to do)
		const distPad = Math.max((maxDist - minDist) * 0.05, 0.1);
		const distRangeFt = (maxDist + distPad) - (minDist - distPad);

		// Vertical exaggeration — keep in sync with CrossSectionWindow.js
		const VE_MULTIPLIER = 10;
		const pxPerFoot = plotWidth / distRangeFt;
		let elevPad = Math.max((maxElev - minElev) * 0.05, 0.1);
		let elevDomainMin = minElev - elevPad;
		let elevDomainMax = maxElev + elevPad;
		let elevRangeFt = elevDomainMax - elevDomainMin;
		let plotHeight = elevRangeFt * pxPerFoot * VE_MULTIPLIER;

		// Enforce minimum plot height; widen Y domain symmetrically to preserve 1:1
		if (plotHeight < MIN_PLOT_HEIGHT) {
			plotHeight = MIN_PLOT_HEIGHT;
			elevRangeFt = plotHeight / pxPerFoot;
			const elevCenter = (minElev + maxElev) / 2;
			elevDomainMin = elevCenter - elevRangeFt / 2;
			elevDomainMax = elevCenter + elevRangeFt / 2;
		}

		const svgHeight = m.top + titleHeight + plotHeight + m.bottom + notesHeight;

		return {
			plotLeft: m.left,
			plotTop: m.top + titleHeight,
			plotWidth,
			plotHeight,
			legendLeft: SVG_WIDTH - m.right + 20,
			legendTop: m.top + titleHeight,
			legendWidth: m.right - 40,
			titleY: m.top - 10 + titleHeight,
			notesY: m.top + titleHeight + plotHeight + m.bottom - 60,
			hasTitle,
			hasNotes,
			svgHeight,
			// Pre-computed domains for _createScales
			distDomainMin: minDist - distPad,
			distDomainMax: maxDist + distPad,
			elevDomainMin,
			elevDomainMax,
		};
	}

	// ─── Scales ────────────────────────────────────────────────────

	static _createScales(profile, layout) {
		const scaleX = d3.scale.linear()
			.domain([layout.distDomainMin, layout.distDomainMax])
			.range([layout.plotLeft, layout.plotLeft + layout.plotWidth]);

		// SVG Y is inverted: higher elevation = lower Y pixel value
		const scaleY = d3.scale.linear()
			.domain([layout.elevDomainMin, layout.elevDomainMax])
			.range([layout.plotTop + layout.plotHeight, layout.plotTop]);

		return { scaleX, scaleY,
			minDist: layout.distDomainMin, maxDist: layout.distDomainMax,
			minElev: layout.elevDomainMin, maxElev: layout.elevDomainMax };
	}

	// ─── Grid ──────────────────────────────────────────────────────

	static _renderGrid(svg, scales, layout) {
		const g = svg.append('g').attr('id', 'layer-grid');
		const color = COLORS.grid;

		const xTicks = scales.scaleX.ticks(Math.floor(layout.plotWidth / 60));
		for (const tick of xTicks) {
			g.append('line')
				.attr('x1', scales.scaleX(tick))
				.attr('y1', layout.plotTop)
				.attr('x2', scales.scaleX(tick))
				.attr('y2', layout.plotTop + layout.plotHeight)
				.attr('stroke', color)
				.attr('stroke-width', 0.5);
		}

		const yTicks = scales.scaleY.ticks(Math.max(Math.floor(layout.plotHeight / 40), 3));
		for (const tick of yTicks) {
			g.append('line')
				.attr('x1', layout.plotLeft)
				.attr('y1', scales.scaleY(tick))
				.attr('x2', layout.plotLeft + layout.plotWidth)
				.attr('y2', scales.scaleY(tick))
				.attr('stroke', color)
				.attr('stroke-width', 0.5);
		}
	}

	// ─── Axes ──────────────────────────────────────────────────────

	static _renderAxes(svg, scales, layout, datumOffset) {
		const g = svg.append('g').attr('id', 'layer-axes');
		const font = FONT_FAMILY;
		const color = COLORS.axis;
		const xAxisY = layout.plotTop + layout.plotHeight;

		// X axis line
		g.append('line')
			.attr('x1', layout.plotLeft)
			.attr('y1', xAxisY)
			.attr('x2', layout.plotLeft + layout.plotWidth)
			.attr('y2', xAxisY)
			.attr('stroke', color)
			.attr('stroke-width', 1.5);

		// X tick marks and labels
		const xTicks = scales.scaleX.ticks(Math.floor(layout.plotWidth / 60));
		for (const tick of xTicks) {
			const x = scales.scaleX(tick);
			g.append('line')
				.attr('x1', x).attr('y1', xAxisY)
				.attr('x2', x).attr('y2', xAxisY + 6)
				.attr('stroke', color)
				.attr('stroke-width', 1);
			g.append('text')
				.attr('x', x)
				.attr('y', xAxisY + 20)
				.attr('text-anchor', 'middle')
				.attr('font-family', font)
				.attr('font-size', '11px')
				.attr('fill', color)
				.text(SVGProfileExporter._formatNumber(tick));
		}

		// X axis label
		g.append('text')
			.attr('x', layout.plotLeft + layout.plotWidth / 2)
			.attr('y', xAxisY + 42)
			.attr('text-anchor', 'middle')
			.attr('font-family', font)
			.attr('font-size', '13px')
			.attr('font-weight', 'bold')
			.attr('fill', color)
			.text('Distance Along Transect (ft)');

		// Y axis line
		g.append('line')
			.attr('x1', layout.plotLeft)
			.attr('y1', layout.plotTop)
			.attr('x2', layout.plotLeft)
			.attr('y2', xAxisY)
			.attr('stroke', color)
			.attr('stroke-width', 1.5);

		// Y tick marks and labels
		const yTicks = scales.scaleY.ticks(Math.max(Math.floor(layout.plotHeight / 40), 3));
		for (const tick of yTicks) {
			const y = scales.scaleY(tick);
			g.append('line')
				.attr('x1', layout.plotLeft - 6).attr('y1', y)
				.attr('x2', layout.plotLeft).attr('y2', y)
				.attr('stroke', color)
				.attr('stroke-width', 1);
			g.append('text')
				.attr('x', layout.plotLeft - 10)
				.attr('y', y + 4)
				.attr('text-anchor', 'end')
				.attr('font-family', font)
				.attr('font-size', '11px')
				.attr('fill', color)
				.text(SVGProfileExporter._formatNumber(tick));
		}

		// Y axis label (rotated)
		let yLabel = 'Elevation (ft)';
		if (datumOffset && datumOffset.applied && datumOffset.reference_landmark_id) {
			yLabel += ` \u2014 Datum: ${datumOffset.reference_landmark_id}`;
		}

		g.append('text')
			.attr('x', -(layout.plotTop + layout.plotHeight / 2))
			.attr('y', 20)
			.attr('text-anchor', 'middle')
			.attr('transform', 'rotate(-90)')
			.attr('font-family', font)
			.attr('font-size', '13px')
			.attr('font-weight', 'bold')
			.attr('fill', color)
			.text(yLabel);

		// Plot border
		g.append('rect')
			.attr('x', layout.plotLeft)
			.attr('y', layout.plotTop)
			.attr('width', layout.plotWidth)
			.attr('height', layout.plotHeight)
			.attr('fill', 'none')
			.attr('stroke', color)
			.attr('stroke-width', 1);
	}

	// ─── Profile Line (Smoothed) ───────────────────────────────────

	static _smoothPoints(points) {
		if (!points || points.length === 0) return [];

		const sorted = points.slice().sort((a, b) => a.distance_along - b.distance_along);
		if (sorted.length <= SMOOTH_TARGET_POINTS) {
			return sorted;
		}

		// Bin points into uniform distance intervals and average elevation per bin
		const minDist = sorted[0].distance_along;
		const maxDist = sorted[sorted.length - 1].distance_along;
		const range = maxDist - minDist;

		if (range <= 0) {
			return [{ distance_along: minDist, elevation: sorted[0].elevation }];
		}

		const numBins = SMOOTH_TARGET_POINTS;
		const binWidth = range / numBins;
		const bins = [];

		for (let i = 0; i < numBins; i++) {
			bins.push({ sumElev: 0, count: 0 });
		}

		for (const pt of sorted) {
			let binIdx = Math.floor((pt.distance_along - minDist) / binWidth);
			if (binIdx >= numBins) binIdx = numBins - 1;
			bins[binIdx].sumElev += pt.elevation;
			bins[binIdx].count += 1;
		}

		const smoothed = [];
		for (let i = 0; i < numBins; i++) {
			if (bins[i].count > 0) {
				smoothed.push({
					distance_along: minDist + (i + 0.5) * binWidth,
					elevation: bins[i].sumElev / bins[i].count,
				});
			}
		}

		return smoothed;
	}

	static _renderProfileLine(svg, points, scales) {
		if (!points || points.length === 0) return;

		const g = svg.append('g').attr('id', 'layer-profile');
		const smoothed = SVGProfileExporter._smoothPoints(points);

		const line = d3.svg.line()
			.x(d => scales.scaleX(d.distance_along))
			.y(d => scales.scaleY(d.elevation))
			.interpolate('monotone');

		g.append('path')
			.datum(smoothed)
			.attr('d', line)
			.attr('fill', 'none')
			.attr('stroke', COLORS.profileLine)
			.attr('stroke-width', 2)
			.attr('stroke-linejoin', 'round');
	}

	static _renderPointScatter(svg, points, scales) {
		const g = svg.append('g').attr('id', 'layer-point-scatter');
		const color = COLORS.pointScatter;

		for (const pt of points) {
			g.append('circle')
				.attr('cx', scales.scaleX(pt.distance_along))
				.attr('cy', scales.scaleY(pt.elevation))
				.attr('r', 1.5)
				.attr('fill', color)
				.attr('fill-opacity', 0.4);
		}
	}

	// ─── Landmarks ─────────────────────────────────────────────────

	static _renderLandmarks(svg, landmarks, scales) {
		const g = svg.append('g').attr('id', 'layer-landmarks');
		const color = COLORS.landmark;
		const font = FONT_FAMILY;

		for (const lm of landmarks) {
			const x = scales.scaleX(lm.distance_along);
			const y = scales.scaleY(lm.elevation);
			const size = 8;

			SVGProfileExporter._drawLandmarkSymbol(g, lm.landmark_type, x, y, size, color);

			if (lm.label) {
				g.append('text')
					.attr('x', x + 4)
					.attr('y', y - size - 4)
					.attr('transform', `rotate(-45, ${x + 4}, ${y - size - 4})`)
					.attr('font-family', font)
					.attr('font-size', '10px')
					.attr('fill', color)
					.text(lm.label);
			}
		}
	}

	static _drawLandmarkSymbol(g, type, cx, cy, size, color) {
		const half = size / 2;

		switch (type) {
			case 'manhole': {
				// Diamond
				const pts = `${cx},${cy - half} ${cx + half},${cy} ${cx},${cy + half} ${cx - half},${cy}`;
				g.append('polygon')
					.attr('points', pts)
					.attr('fill', color)
					.attr('stroke', color)
					.attr('stroke-width', 1);
				break;
			}
			case 'catch_basin': {
				// Upward triangle
				const pts = `${cx},${cy - half} ${cx + half},${cy + half} ${cx - half},${cy + half}`;
				g.append('polygon')
					.attr('points', pts)
					.attr('fill', color)
					.attr('stroke', color)
					.attr('stroke-width', 1);
				break;
			}
			default: {
				// Circle
				g.append('circle')
					.attr('cx', cx)
					.attr('cy', cy)
					.attr('r', half)
					.attr('fill', color)
					.attr('stroke', color)
					.attr('stroke-width', 1);
				break;
			}
		}
	}

	// ─── Curb Reveals ──────────────────────────────────────────────

	static _renderCurbReveals(svg, curbReveals, scales, layout) {
		const g = svg.append('g').attr('id', 'layer-curb-reveals');
		const color = COLORS.curbReveal;
		const font = FONT_FAMILY;

		for (const cr of curbReveals) {
			const x = scales.scaleX(cr.distance_along);

			// Vertical dashed line through the plot area (matches in-viewer style)
			g.append('line')
				.attr('x1', x).attr('y1', layout.plotTop)
				.attr('x2', x).attr('y2', layout.plotTop + layout.plotHeight)
				.attr('stroke', color)
				.attr('stroke-width', 1.5)
				.attr('stroke-dasharray', '4,3');

			// Height label near the top of the line
			const label = `${SVGProfileExporter._formatNumber(cr.curb_height)} in`;
			g.append('text')
				.attr('x', x + 3)
				.attr('y', layout.plotTop + 14)
				.attr('font-family', font)
				.attr('font-size', '10px')
				.attr('fill', color)
				.attr('font-weight', 'bold')
				.text(label);

			// Side label below the height label
			if (cr.side) {
				g.append('text')
					.attr('x', x + 3)
					.attr('y', layout.plotTop + 26)
					.attr('font-family', font)
					.attr('font-size', '9px')
					.attr('fill', color)
					.text(cr.side);
			}
		}
	}

	// ─── Datum References ──────────────────────────────────────────

	static _renderDatumReferences(svg, datumLandmarks, scales, layout) {
		const g = svg.append('g').attr('id', 'layer-datum-refs');
		const color = COLORS.datumRef;
		const font = FONT_FAMILY;

		for (const lm of datumLandmarks) {
			const refElev = lm.datum.reference_elevation;
			const y = scales.scaleY(refElev);

			// Dashed horizontal line across full plot
			g.append('line')
				.attr('x1', layout.plotLeft)
				.attr('y1', y)
				.attr('x2', layout.plotLeft + layout.plotWidth)
				.attr('y2', y)
				.attr('stroke', color)
				.attr('stroke-width', 1.5)
				.attr('stroke-dasharray', '8,4');

			// Label at right edge
			const label = lm.datum.reference_label
				? `${lm.datum.reference_label}: ${SVGProfileExporter._formatNumber(refElev)} ft`
				: `Datum: ${SVGProfileExporter._formatNumber(refElev)} ft`;

			g.append('text')
				.attr('x', layout.plotLeft + layout.plotWidth - 5)
				.attr('y', y - 5)
				.attr('text-anchor', 'end')
				.attr('font-family', font)
				.attr('font-size', '10px')
				.attr('fill', color)
				.attr('font-weight', 'bold')
				.text(label);
		}
	}

	// ─── Derived Landmarks ─────────────────────────────────────────

	static _renderDerivedLandmarks(svg, derivedLandmarks, scales, allLandmarks) {
		const g = svg.append('g').attr('id', 'layer-derived-landmarks');
		const color = COLORS.derivedLandmark;
		const font = FONT_FAMILY;

		for (const lm of derivedLandmarks) {
			const x = scales.scaleX(lm.distance_along);
			const y = scales.scaleY(lm.elevation);
			const half = 6;

			// Downward-pointing triangle
			g.append('polygon')
				.attr('points',
					`${x},${y + half} ${x + half},${y - half} ${x - half},${y - half}`)
				.attr('fill', color)
				.attr('stroke', color)
				.attr('stroke-width', 1);

			// Dashed connector line to parent landmark
			const parent = allLandmarks.find(p => p.landmark_id === lm.derived_from);
			if (parent) {
				const parentY = scales.scaleY(parent.elevation);
				g.append('line')
					.attr('x1', x)
					.attr('y1', y - half)
					.attr('x2', x)
					.attr('y2', parentY)
					.attr('stroke', color)
					.attr('stroke-width', 1)
					.attr('stroke-dasharray', '4,3');
			}

			// Label
			if (lm.label) {
				g.append('text')
					.attr('x', x + 8)
					.attr('y', y + 4)
					.attr('font-family', font)
					.attr('font-size', '10px')
					.attr('fill', color)
					.text(`${lm.label}: ${SVGProfileExporter._formatNumber(lm.elevation)} ft`);
			}
		}
	}

	// ─── High/Low Points ───────────────────────────────────────────

	static _renderHighLowPoints(svg, metrics, scales) {
		const g = svg.append('g').attr('id', 'layer-high-low');
		const font = FONT_FAMILY;

		if (metrics.high_point) {
			const hp = metrics.high_point;
			const x = scales.scaleX(hp.distance_along);
			const y = scales.scaleY(hp.elevation);

			g.append('circle')
				.attr('cx', x).attr('cy', y)
				.attr('r', 4)
				.attr('fill', 'none')
				.attr('stroke', COLORS.highPoint)
				.attr('stroke-width', 2);

			SVGProfileExporter._renderCallout(
				g, x, y,
				`HIGH: ${SVGProfileExporter._formatNumber(hp.elevation)} ft`,
				COLORS.highPoint, font, -30
			);
		}

		if (metrics.low_point) {
			const lp = metrics.low_point;
			const x = scales.scaleX(lp.distance_along);
			const y = scales.scaleY(lp.elevation);

			g.append('circle')
				.attr('cx', x).attr('cy', y)
				.attr('r', 4)
				.attr('fill', 'none')
				.attr('stroke', COLORS.lowPoint)
				.attr('stroke-width', 2);

			SVGProfileExporter._renderCallout(
				g, x, y,
				`LOW: ${SVGProfileExporter._formatNumber(lp.elevation)} ft`,
				COLORS.lowPoint, font, 30
			);
		}
	}

	static _renderCallout(g, x, y, text, color, font, offsetY) {
		const textX = x + 10;
		const textY = y + offsetY;

		// Arrow line
		g.append('line')
			.attr('x1', x).attr('y1', y)
			.attr('x2', textX).attr('y2', textY)
			.attr('stroke', color)
			.attr('stroke-width', 1);

		// Background rect for readability
		const textLen = text.length * 6.5 + 10;
		g.append('rect')
			.attr('x', textX - 2)
			.attr('y', textY - 12)
			.attr('width', textLen)
			.attr('height', 16)
			.attr('fill', '#ffffff')
			.attr('stroke', color)
			.attr('stroke-width', 0.5)
			.attr('rx', 2);

		g.append('text')
			.attr('x', textX + 3)
			.attr('y', textY)
			.attr('font-family', font)
			.attr('font-size', '10px')
			.attr('font-weight', 'bold')
			.attr('fill', color)
			.text(text);
	}

	// ─── Legend ─────────────────────────────────────────────────────

	static _renderLegend(svg, activeLayers, layout) {
		const g = svg.append('g').attr('id', 'layer-legend');
		const font = FONT_FAMILY;
		const C = COLORS;

		const legendEntries = {
			landmarks: {
				label: 'Landmarks',
				drawSymbol: (parent, x, y) => {
					parent.append('circle')
						.attr('cx', x + 6).attr('cy', y)
						.attr('r', 5).attr('fill', C.landmark);
				},
			},
			curbReveals: {
				label: 'Curb Reveals',
				drawSymbol: (parent, x, y) => {
					parent.append('line')
						.attr('x1', x + 1).attr('y1', y - 6)
						.attr('x2', x + 1).attr('y2', y + 6)
						.attr('stroke', C.curbReveal).attr('stroke-width', 3);
					parent.append('line')
						.attr('x1', x + 10).attr('y1', y - 6)
						.attr('x2', x + 10).attr('y2', y + 6)
						.attr('stroke', C.curbReveal).attr('stroke-width', 3);
				},
			},
			datumReferences: {
				label: 'Datum References',
				drawSymbol: (parent, x, y) => {
					parent.append('line')
						.attr('x1', x).attr('y1', y)
						.attr('x2', x + 16).attr('y2', y)
						.attr('stroke', C.datumRef)
						.attr('stroke-width', 1.5)
						.attr('stroke-dasharray', '4,2');
				},
			},
			derivedLandmarks: {
				label: 'Derived Landmarks',
				drawSymbol: (parent, x, y) => {
					parent.append('polygon')
						.attr('points', `${x + 6},${y + 5} ${x + 11},${y - 5} ${x + 1},${y - 5}`)
						.attr('fill', C.derivedLandmark);
				},
			},
			highLowPoints: {
				label: 'High / Low Points',
				drawSymbol: (parent, x, y) => {
					parent.append('circle')
						.attr('cx', x + 4).attr('cy', y).attr('r', 4)
						.attr('fill', 'none').attr('stroke', C.highPoint).attr('stroke-width', 2);
					parent.append('circle')
						.attr('cx', x + 14).attr('cy', y).attr('r', 4)
						.attr('fill', 'none').attr('stroke', C.lowPoint).attr('stroke-width', 2);
				},
			},
		};

		// Title
		g.append('text')
			.attr('x', layout.legendLeft)
			.attr('y', layout.legendTop + 5)
			.attr('font-family', font)
			.attr('font-size', '13px')
			.attr('font-weight', 'bold')
			.attr('fill', '#333333')
			.text('Legend');

		// Separator
		g.append('line')
			.attr('x1', layout.legendLeft)
			.attr('y1', layout.legendTop + 12)
			.attr('x2', layout.legendLeft + layout.legendWidth)
			.attr('y2', layout.legendTop + 12)
			.attr('stroke', '#cccccc')
			.attr('stroke-width', 1);

		let yPos = layout.legendTop + 32;

		for (const layerKey of activeLayers) {
			const entry = legendEntries[layerKey];
			if (!entry) continue;

			entry.drawSymbol(g, layout.legendLeft, yPos);

			g.append('text')
				.attr('x', layout.legendLeft + 22)
				.attr('y', yPos + 4)
				.attr('font-family', font)
				.attr('font-size', '11px')
				.attr('fill', '#333333')
				.text(entry.label);

			yPos += 28;
		}
	}

	// ─── Title Block ───────────────────────────────────────────────

	static _renderTitleBlock(svg, title, layout) {
		const g = svg.append('g').attr('id', 'layer-title');
		const font = FONT_FAMILY;

		g.append('text')
			.attr('x', SVG_WIDTH / 2)
			.attr('y', 30)
			.attr('text-anchor', 'middle')
			.attr('font-family', font)
			.attr('font-size', '18px')
			.attr('font-weight', 'bold')
			.attr('fill', '#333333')
			.text(title);
	}

	// ─── Notes ─────────────────────────────────────────────────────

	static _renderNotes(svg, notes, layout) {
		const g = svg.append('g').attr('id', 'layer-notes');
		const font = FONT_FAMILY;
		const color = COLORS.notes;

		const lines = notes.split('\n');
		const startY = layout.notesY;

		for (let i = 0; i < lines.length; i++) {
			g.append('text')
				.attr('x', layout.plotLeft)
				.attr('y', startY + (i * 14))
				.attr('font-family', font)
				.attr('font-size', '10px')
				.attr('fill', color)
				.text(lines[i]);
		}
	}

	// ─── Utilities ─────────────────────────────────────────────────

	/**
	 * Format a number for display: up to 2 decimal places, no trailing zeros.
	 */
	static _formatNumber(n) {
		if (n == null || isNaN(n)) return '\u2014';
		return parseFloat(n.toFixed(2)).toString();
	}
}
